import { format, isValid } from "date-fns";
import { iDataTableColumn } from "../../components/reusable-components/data-table/DataTable";
import {
  EventStatusEnum,
  iEventType,
  iFlatEvent,
} from "../../types/eventTypes";

export const getBudgetValueToDisplay = (event: iEventType) => {
  return event.request
    ? event?.request?.price.toFixed(2)
    : event?.timeOfDay?.value
    ? event.budgetType?.value && event.budget === event.maxBudget
      ? event.budgetType?.value === 'ELITE_AED_500' || event.budgetType?.value === 'ELITE_AED_600'
        ? 700 * event.guestsCount
        : event.maxBudget * event.guestsCount
      : event.budget
    : event.maxBudget;
};

export const getFlatEvent = (
  event: Array<iEventType> | [],
  getCmsValue: (value: string | null) => string,
  isDeclinedGrid?: boolean,
): Array<iFlatEvent> | [] => {
  return event?.length > 0 ? event.map((item) => {
    return {
      id: item.id,
      name: item.request ? item.request.name + "1" : item.name,
      city: getCmsValue(item.city + ""),
      customerName: item.request
        ? item.request.customerName
        : `${item.customer.firstName}   ${item.customer.lastName || ""}`,
      eventDate: item.eventDate && new Date(item.eventDate),
      guestsCount: item.request ? item.request.guestCount : item.guestsCount,
      budget: getBudgetValueToDisplay(item),
      kindOfFood: getStringSortedEventTypeOfFood(
        item.typeOfFoods
          ?.concat(
            item?.typeOfCuisines,
            item.otherKindOfFoods?.value?.trim()
              ? {
                  ...item.otherKindOfFoods,
                  noCms: true,
                }
              : [],
          )
          .filter((item) => item),
        getCmsValue,
      ),
      occasionType:
        item?.serviceEventType?.value === "OTHER"
          ? item.otherServiceEventType.value
          : getCmsValue(item?.serviceEventType?.value),
      serviceType: getCmsValue(item?.providerType?.value),
      showIndicator: item.notificationCount > 0,
      isFlexible: item.isBudgetFlexible,
      createdAt: item.createdAt,
      ...(!!isDeclinedGrid
        ? {
            declinedBy:
              item.status === EventStatusEnum.Cancelled
                ? declinedByHash[3]
                : (declinedByHash[item.vendorEventStatus] as string) || "",
          }
        : {}),
    } as iFlatEvent;
  }) : [];
};

export const getStringSortedEventTypeOfFood = (
  array: { value: string; noCms?: boolean }[],
  getCmsValue: (value: string | null) => string,
) => {
  const foodTypes = array
    .map((item) => (item.noCms ? item.value : getCmsValue(item?.value) || ""))
    ?.filter((item) => item)
    .sort();
  return foodTypes;
};
const declinedByHash: { [key: number]: string } = {
  4: "Self",
  3: "Customer",
  5: "Expired",
};

export const EVENT_GRID_COLUMN_FIELDS_KEYS = {
  EVENT_DATE: "eventDate",
  BUDGET: "budget",
  GUEST_COUNT: "guestsCount",
  OCCASION_TYPE: "occasionType",
  SERVICE_TYPE: "serviceType",
  KIND_OF_FOOD: "kindOfFood",
  CITY: "city",
  NAME: "name",
  CUSTOMER_NAME: "customerName",
  DECLINE_BY: "declinedBy",
};

export const EVENT_GRID_COLUMN_FIELDS_VALUES = {
  [EVENT_GRID_COLUMN_FIELDS_KEYS.EVENT_DATE]: "Date",
  [EVENT_GRID_COLUMN_FIELDS_KEYS.BUDGET]: "Budget",
  [EVENT_GRID_COLUMN_FIELDS_KEYS.GUEST_COUNT]: "Guests",
  [EVENT_GRID_COLUMN_FIELDS_KEYS.OCCASION_TYPE]: "Occasion type",
  [EVENT_GRID_COLUMN_FIELDS_KEYS.SERVICE_TYPE]: "Service type",
  [EVENT_GRID_COLUMN_FIELDS_KEYS.KIND_OF_FOOD]: "Type of food",
  [EVENT_GRID_COLUMN_FIELDS_KEYS.CITY]: "City",
  [EVENT_GRID_COLUMN_FIELDS_KEYS.NAME]: "Event name",
  [EVENT_GRID_COLUMN_FIELDS_KEYS.CUSTOMER_NAME]: "Customer name",
  [EVENT_GRID_COLUMN_FIELDS_KEYS.DECLINE_BY]: "Declined by",
};

const formatDay = (value: Date | string | null | undefined) => {
  if (!value) return "";
  const date = new Date(value);
  return isValid(date) ? format(date, "dd MMM yyyy") : "";
};

const toDate = (value: Date | string | null | undefined) => (value ? new Date(value) : null);

const label = (key: string) => EVENT_GRID_COLUMN_FIELDS_VALUES[key as keyof typeof EVENT_GRID_COLUMN_FIELDS_VALUES];

export const getColumns = (showDecline?: boolean): iDataTableColumn<iFlatEvent>[] => {
  const columns: iDataTableColumn<iFlatEvent>[] = [
    {
      key: EVENT_GRID_COLUMN_FIELDS_KEYS.EVENT_DATE,
      label: label(EVENT_GRID_COLUMN_FIELDS_KEYS.EVENT_DATE),
      minWidth: 150,
      sortValue: (row) => toDate(row.eventDate),
      sortType: "date",
      searchValue: (row) => formatDay(row.eventDate),
      render: (row) => formatDay(row.eventDate),
    },
    {
      key: EVENT_GRID_COLUMN_FIELDS_KEYS.BUDGET,
      label: label(EVENT_GRID_COLUMN_FIELDS_KEYS.BUDGET),
      minWidth: 110,
      sortValue: (row) => Number(row.budget) || 0,
      sortType: "number",
      searchValue: (row) => String(row.budget ?? ""),
      render: (row) => row.budget,
    },
    {
      key: EVENT_GRID_COLUMN_FIELDS_KEYS.GUEST_COUNT,
      label: label(EVENT_GRID_COLUMN_FIELDS_KEYS.GUEST_COUNT),
      minWidth: 90,
      sortValue: (row) => Number(row.guestsCount) || 0,
      sortType: "number",
      render: (row) => row.guestsCount,
    },
    {
      key: EVENT_GRID_COLUMN_FIELDS_KEYS.OCCASION_TYPE,
      label: label(EVENT_GRID_COLUMN_FIELDS_KEYS.OCCASION_TYPE),
      minWidth: 160,
      searchValue: (row) => row.occasionType,
      render: (row) => row.occasionType,
    },
    {
      key: EVENT_GRID_COLUMN_FIELDS_KEYS.SERVICE_TYPE,
      label: label(EVENT_GRID_COLUMN_FIELDS_KEYS.SERVICE_TYPE),
      minWidth: 160,
      searchValue: (row) => row.serviceType,
      render: (row) => row.serviceType,
    },
    {
      key: EVENT_GRID_COLUMN_FIELDS_KEYS.KIND_OF_FOOD,
      label: label(EVENT_GRID_COLUMN_FIELDS_KEYS.KIND_OF_FOOD),
      minWidth: 180,
      searchValue: (row) => (row.kindOfFood ?? []).join(", "),
      render: (row) => (row.kindOfFood ?? []).join(", "),
    },
    {
      key: EVENT_GRID_COLUMN_FIELDS_KEYS.CITY,
      label: label(EVENT_GRID_COLUMN_FIELDS_KEYS.CITY),
      minWidth: 130,
      sortValue: (row) => row.city,
      render: (row) => row.city,
    },
    {
      key: EVENT_GRID_COLUMN_FIELDS_KEYS.NAME,
      label: label(EVENT_GRID_COLUMN_FIELDS_KEYS.NAME),
      minWidth: 240,
      sortValue: (row) => row.name,
      render: (row) => row.name,
    },
    {
      key: EVENT_GRID_COLUMN_FIELDS_KEYS.CUSTOMER_NAME,
      label: label(EVENT_GRID_COLUMN_FIELDS_KEYS.CUSTOMER_NAME),
      minWidth: 160,
      sortValue: (row) => row.customerName,
      render: (row) => row.customerName,
    },
  ];
  if (showDecline) {
    columns.push({
      key: EVENT_GRID_COLUMN_FIELDS_KEYS.DECLINE_BY,
      label: label(EVENT_GRID_COLUMN_FIELDS_KEYS.DECLINE_BY),
      minWidth: 140,
      sortValue: (row) => row.declinedBy,
      render: (row) => row.declinedBy,
    });
  }

  return columns;
};
